import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { PageHeader } from '@/modules/common/components/PageHeader';
import { EmployeeLearningBudgetDialog } from '@/modules/employees/components/EmployeeLearningBudgetDialog';
import { InviteUserDialog } from '@/modules/employees/components/InviteUserDialog';
import { RemoveTeamMemberDialog } from '@/modules/employees/components/RemoveTeamMemberDialog';
import { TeamDialog } from '@/modules/employees/components/TeamDialog';
import { TeamMembersTable } from '@/modules/employees/components/TeamMembersTable';
import { UploadEmployeesDialog } from '@/modules/employees/components/UploadEmployeesDialog';
import { IUserWithProfile } from '@/types/user';
import {
  ArrowBackIosOutlined,
  Edit,
  ExpandMore,
  PersonAddOutlined,
  SyncAlt,
  Upload,
} from '@mui/icons-material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';
import { useLearningBudgetManagement } from '../hooks/useLearningBudgetManagement';
import { useGetTeam } from '../hooks/useTeams';

type Params = {
  teamId: string;
};

const routeApi = getRouteApi('/_admin/teams/$teamId');

export function ViewTeamPage() {
  const { teamId } = routeApi.useParams<Params>();
  const search = routeApi.useSearch();
  const userContext = useCurrentUserContext();

  const [learningBudgetEmployeeId, setLearningBudgetEmployeeId] = useState('');
  const [removeMemberId, setRemoveMemberId] = useState('');
  useState(false);
  const [isInviteUserDialogOpen, setIsInviteUserDialogOpen] = useState(false);
  const [isUploadEmployeesDialogOpen, setIsUploadEmployeesDialogOpen] =
    useState(false);
  const [isEditTeamDialogOpen, setIsEditTeamDialogOpen] = useState(
    Boolean((search as { edit?: string }).edit),
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { isLoading: isLoadingTeam, data: { data: team } = {} } =
    useGetTeam(teamId);

  const { showModal: showLearningBudgetModal, Modal: LearningBudgetModal } =
    useLearningBudgetManagement();

  const members = (team?.members as IUserWithProfile[]) || [];

  if (!team || isLoadingTeam) {
    return <CircularProgress />;
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        pageTitle={
          <div className="flex items-center gap-1">
            <IconButton
              type="button"
              aria-label="Back"
              onClick={() => window.history.back()}
              size="small"
            >
              <ArrowBackIosOutlined className="text-base" />
            </IconButton>
            <Typography variant="h1">Team {team.name}</Typography>
          </div>
        }
      >
        <Button
          startIcon={<Edit />}
          onClick={() => setIsEditTeamDialogOpen(true)}
          variant="outlined"
        >
          Edit team
        </Button>
        <ButtonGroup
          variant="contained"
          disableElevation
          size="small"
          className="ml-2"
        >
          <Button
            startIcon={<PersonAddOutlined />}
            onClick={() => setIsInviteUserDialogOpen(true)}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Invite members
          </Button>

          <Button
            aria-controls={anchorEl ? 'invite-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            onClick={handleMenuOpen}
            variant="outlined"
            style={{
              minWidth: 40,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <ExpandMore />
          </Button>
        </ButtonGroup>
        <Menu
          id="invite-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ list: { 'aria-labelledby': 'invite-menu' } }}
        >
          <MenuItem
            onClick={() => {
              setIsUploadEmployeesDialogOpen(true);
              handleMenuClose();
            }}
            className="flex gap-2"
          >
            <Upload style={{ marginRight: 8 }} /> Upload CSV file
          </MenuItem>
        </Menu>
      </PageHeader>
      <div className="flex-1 flex flex-col">
        <header className="flex gap-2 justify-between items-start py-6 px-8 border-b">
          <div className="flex flex-col gap-2">
            <h2 className="font-semibold text-xl">Team Members</h2>
            <p className="text-text">Invite and manage members of your team.</p>
          </div>
          <div className="flex justify-end items-center gap-2">
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => showLearningBudgetModal({ variant: 'team', team })}
              className="self-center shrink-0"
              startIcon={<AccountBalanceWalletOutlinedIcon />}
            >
              Manage team LB funds
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              href={`/learning-budgets/transactions?teamId=${teamId}`}
              className="self-center shrink-0"
              startIcon={<SyncAlt />}
            >
              View team LB transactions
            </Button>
          </div>
        </header>

        <div className="px-8 py-6 flex-1">
          <TeamMembersTable
            tableOptions={{
              data: members,
              state: { isLoading: isLoadingTeam },
            }}
            learningBudgetLimit={team.learningBudgetLimit}
            onSetLearningBudget={user => setLearningBudgetEmployeeId(user._id!)}
            onRemoveMember={user => setRemoveMemberId(user._id!)}
          />
        </div>
      </div>
      <EmployeeLearningBudgetDialog
        open={!!learningBudgetEmployeeId}
        onClose={() => setLearningBudgetEmployeeId('')}
        onSuccess={() => {
          setLearningBudgetEmployeeId('');
        }}
        employee={learningBudgetEmployeeId}
      />
      <RemoveTeamMemberDialog
        open={!!removeMemberId}
        onClose={() => setRemoveMemberId('')}
        onSuccess={() => {
          setRemoveMemberId('');
        }}
        team={teamId}
        user={removeMemberId}
      />
      <InviteUserDialog
        open={isInviteUserDialogOpen}
        onClose={() => setIsInviteUserDialogOpen(false)}
        onSuccess={() => {
          setIsInviteUserDialogOpen(false);
        }}
      />
      <UploadEmployeesDialog
        open={isUploadEmployeesDialogOpen}
        onClose={() => setIsUploadEmployeesDialogOpen(false)}
        company={userContext!.employer!._id!}
        onSuccess={() => {
          setIsUploadEmployeesDialogOpen(false);
        }}
      />
      <TeamDialog
        open={isEditTeamDialogOpen}
        onClose={() => setIsEditTeamDialogOpen(false)}
        onSuccess={() => {
          setIsEditTeamDialogOpen(false);
        }}
        team={team}
      />
      <LearningBudgetModal />
    </div>
  );
}
